import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => {
      // Authenticated users go to their role-specific home
      const auth = useAuthStore();
      if (!auth.user) return '/login';
      if (auth.user.role === 'admin') return '/admin';
      if (auth.user.status === 'pending') return '/waitlist';
      if (auth.user.status === 'rejected') return '/login?rejected=1';
      return '/inbox';
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { public: true },
  },
  {
    path: '/waitlist',
    name: 'waitlist',
    component: () => import('@/views/auth/WaitlistView.vue'),
    meta: { requiresAuth: true, role: 'client' },
  },
  {
    path: '/inbox',
    name: 'inbox',
    component: () => import('@/views/client/InboxView.vue'),
    meta: { requiresAuth: true, role: 'client', requiresApproved: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/client/SettingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, role: 'admin' },
    children: [
      { path: '', redirect: '/admin/all-mail' },
      { path: 'all-mail', name: 'admin.allmail', component: () => import('@/views/admin/AllMailView.vue') },
      { path: 'clients', name: 'admin.clients', component: () => import('@/views/admin/ClientsView.vue') },
      { path: 'clients/:id', name: 'admin.client', component: () => import('@/views/admin/ClientDetailView.vue') },
      { path: 'trash', name: 'admin.trash', component: () => import('@/views/admin/TrashView.vue') },
      { path: 'settings', name: 'admin.settings', component: () => import('@/views/admin/SettingsView.vue') },
    ],
  },
  {
    path: '/:catchAll(.*)',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
    meta: { public: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.user) {
    await auth.fetchMe().catch(() => {});
  }
  const user = auth.user;

  if (to.meta.requiresAuth && !user) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.meta.public && user) {
    if (user.role === 'admin') return { name: 'admin.allmail' };
    if (user.status === 'pending') return { name: 'waitlist' };
    return { name: 'inbox' };
  }
  if (to.meta.role === 'admin' && user?.role !== 'admin') {
    return { name: 'inbox' };
  }
  if (to.meta.role === 'client' && user?.role !== 'client') {
    return { name: 'admin.allmail' };
  }
  if (to.meta.requiresApproved && user?.role === 'client' && user.status !== 'approved') {
    return { name: 'waitlist' };
  }
  return true;
});